import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import {
    getMotivationInfoThunk,
    getOrderAmountListThunk,
    getSchedulerGetMarketsWithoutRouteThunk,
    getSchedulerMyRoutesThunk,
    getSchedulerSearchMarketsWithoutRouteThunk,
    getUserTeamGetByManagerThunk,
} from "tm-shared/api";
import { displayDateFormat } from "tm-shared/constants";
import { Schedule } from "tm-shared/types";

import {
    getKpiGetProgressSvThunk,
    getKpiGetSalaryToPaySvThunk,
    getSchedulerAllScheduleSvThunk,
} from "./sv.slice.thunks";
import { SVState } from "./sv.slice.types";

const initialState: SVState = {
    team: null,
    routeList: {},
    currentScheduleList: [],
    marketsWithoutRoute: [],
    checkoutAgentsList: [],
    searchAgent: "",
    currentUser: "",
    checkoutTotals: {
        opened: 0,
        closed: 0,
    },

    mmlStatistics: null,
    mmlStatisticsDate: "",
    mmlStatisticsError: "",
    motivation: null,
    motivationError: "",
    salary: null,
    salaryError: "",
    motivationInfoDate: "",
    lastUpdated: {
        checkouts: null,
        team: null,
        activeSchedule: null,
    },
    activeCollapse: null,
    members: {},
    activeSchedule: null,
};

export const SVSlice = createSlice({
    name: "sv",
    initialState,
    reducers: {
        setSearchAgent(state, action: PayloadAction<string>) {
            state.searchAgent = action.payload;
        },
        setCurrentUser(state, action: PayloadAction<string>) {
            state.currentUser = action.payload;
        },
        setCurrentScheduleList(state, action: PayloadAction<Schedule[]>) {
            state.currentScheduleList = action.payload;
        },
        clearCurrentScheduleList(state) {
            state.currentScheduleList = [];
        },
        updateSchedule(
            state,
            action: PayloadAction<{
                marketId: string;
                updatedData: Partial<Schedule>;
            }>,
        ) {
            const { marketId, updatedData } = action.payload;
            const routeIndex = state.currentScheduleList.findIndex(
                (route) => route.marketId === marketId,
            );

            if (routeIndex !== -1) {
                state.currentScheduleList[routeIndex] = {
                    ...state.currentScheduleList[routeIndex],
                    ...updatedData,
                };
            }
        },
        deleteSchedules(state, action: PayloadAction<string[]>) {
            const marketIds = action.payload;
            state.currentScheduleList = state.currentScheduleList.filter(
                (route) => !marketIds.includes(route.marketId),
            );
        },

        setSVActiveCollapse: (state, action: PayloadAction<string | null>) => {
            state.activeCollapse = action.payload;
        },

        setSVActiveSchedule: (state, action: PayloadAction<string | null>) => {
            state.activeSchedule = action.payload;
            state.lastUpdated.activeSchedule = dayjs().format(
                "DD.MM.YYYY HH:mm:ss",
            );
        },
        clearSVActiveSchedule: (state) => {
            state.activeSchedule = null;
            state.lastUpdated.activeSchedule = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(
                getSchedulerAllScheduleSvThunk.fulfilled,
                (state, action) => {
                    state.currentScheduleList = action.payload.result;
                },
            )
            .addCase(
                getUserTeamGetByManagerThunk.fulfilled,
                (state, action) => {
                    state.team = action.payload.result;
                    state.lastUpdated.team = dayjs().format(
                        displayDateFormat.dateTimeSeconds,
                    );
                },
            )
            .addCase(
                getSchedulerMyRoutesThunk.fulfilled,
                (state, { payload }) => {
                    state.routeList = payload.result.reduce((acc, route) => {
                        acc[route.uuid] = route;
                        return acc;
                    }, {});
                },
            )

            .addCase(
                getSchedulerGetMarketsWithoutRouteThunk.fulfilled,
                (state, action) => {
                    state.marketsWithoutRoute = action.payload.result;
                },
            )

            .addCase(getOrderAmountListThunk.fulfilled, (state, action) => {
                state.checkoutAgentsList = action.payload.result;
                state.checkoutTotals = action.payload.result.reduce(
                    (totals, item) => {
                        totals.opened += item.amountOpened || 0;
                        totals.closed += item.amountClosed || 0;
                        return totals;
                    },
                    { opened: 0, closed: 0 },
                );
                state.lastUpdated.checkouts = dayjs().format(
                    displayDateFormat.dateTimeSeconds,
                );
            })

            .addCase(
                getSchedulerSearchMarketsWithoutRouteThunk.fulfilled,
                (state, action) => {
                    state.marketsWithoutRoute = action.payload.result;
                },
            )

            .addCase(getKpiGetProgressSvThunk.fulfilled, (state, action) => {
                state.motivation = action.payload.result;

                state.motivationError = "";
            })

            .addCase(getKpiGetProgressSvThunk.rejected, (state) => {
                state.motivationError =
                    "План мотивації наразі не розраховано! Для уточнення зверніться до керівника";
                state.motivation = null;
            })

            .addCase(getKpiGetSalaryToPaySvThunk.fulfilled, (state, action) => {
                state.salary = action.payload.result;
                state.salaryError = "";
            })
            .addCase(getKpiGetSalaryToPaySvThunk.rejected, (state) => {
                state.salaryError =
                    "Зарплатні дані наразі відсутні! Для уточнення зверніться до керівника";
                state.salary = null;
            })
            .addCase(getMotivationInfoThunk.fulfilled, (state, { payload }) => {
                const result = payload?.result?.state?.groups;

                if (!result) return;
                state.motivationInfoDate = payload?.result?.startDate;
            });
    },
});

export const {
    setCurrentScheduleList,
    clearCurrentScheduleList,
    updateSchedule,
    deleteSchedules,
    setSearchAgent,
    setCurrentUser,
    setSVActiveCollapse,
    setSVActiveSchedule,
    clearSVActiveSchedule,
} = SVSlice.actions;

export default SVSlice.reducer;
